import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  ConversationPlatform,
  getConversationStatus,
  MinimalConversation,
  textForDeletedFacebookComment,
} from "@redotech/redo-model/conversation";
import { getCustomerDisplayName } from "@redotech/redo-model/customer";
import { CardListCard, CardListCardProps } from "@redotech/redo-web/card-list";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { RedoSupportChannelBadge } from "@redotech/redo-web/support/redo-support-channel-badge";
import { RedoSupportStatusBadge } from "@redotech/redo-web/support/redo-support-status-badge";
import { Text } from "@redotech/redo-web/text";
import { formatDateTime } from "@redotech/redo-web/time";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { formatTimeAgo } from "@redotech/util/date-format";
import classNames from "classnames";
import {
  memo,
  MouseEventHandler,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { ActiveUsers } from "./active-users";
import * as conversationInfoCardCss from "./conversation-info-card.module.css";
import { markConversationAsRead } from "./conversations/mark-conversation-read-manager";
import { TeamConversationActivityContext } from "./support";
import { removeSimpleHTMLTags } from "./utils";

export const ConversationInfoCardRender = ({
  item,
  onClick,
  loading,
  empty,
  isActive,
  key,
}: CardListCardProps<MinimalConversation>): ReactElement => {
  "use no memo";
  return (
    <ConversationInfoCard
      empty={empty}
      isActive={isActive}
      item={item}
      key={key}
      loading={loading}
      onClick={onClick}
    />
  );
};

export const ConversationInfoCard: CardListCard<MinimalConversation> = memo(
  function ConversationInfoCard({
    item,
    onClick,
    loading,
    empty,
    isActive,
  }: CardListCardProps<MinimalConversation>) {
    const user = useRequiredContext(UserContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const team = useContext(TeamContext);
    const conversation = item;
    const lastMessage = conversation?.lastMessage || null;

    const [timeSinceMessage, setTimeSinceMessage] = useState<string>(
      formatTimeAgo(lastMessage?.sentAt),
    );
    let contentLines;
    if (lastMessage) {
      const lastMessageContent = lastMessage.facebook?.comment?.deleted
        ? textForDeletedFacebookComment
        : lastMessage?.content;
      contentLines = removeSimpleHTMLTags(lastMessageContent).split("\n");
    }
    const active = item && isActive ? isActive(item) : false;

    const teamConversationActivity = useContext(
      TeamConversationActivityContext,
    );
    const activeUsers = conversation
      ? teamConversationActivity?.[conversation._id]?.viewing.filter(
          (userId) => userId !== user._id,
        ) || []
      : [];

    useEffect(() => {
      if (conversation && !conversation.read && active && !!team) {
        void markConversationAsRead(client, conversation, team, user._id);
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage?._id]);

    useEffect(() => {
      if (!lastMessage?.sentAt) {
        return;
      }
      setTimeSinceMessage(formatTimeAgo(lastMessage.sentAt));
      const intervalId = setInterval(() => {
        setTimeSinceMessage(formatTimeAgo(lastMessage.sentAt));
      }, 60 * 1000);
      return () => clearInterval(intervalId);
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage?._id]);

    return (
      <div
        className={classNames(conversationInfoCardCss.container, {
          [conversationInfoCardCss.selected!]: active,
          [conversationInfoCardCss.loading!]: loading,
        })}
        onClick={onClick as MouseEventHandler<HTMLDivElement> | undefined}
      >
        {conversation && item && (
          <div className={conversationInfoCardCss.info}>
            <div className={conversationInfoCardCss.text}>
              <div className={conversationInfoCardCss.personName}>
                <div className={conversationInfoCardCss.header}>
                  <Text
                    className={conversationInfoCardCss.customerTitle}
                    fontSize="xs"
                    fontWeight="semibold"
                    textColor="primary"
                  >
                    {getCustomerDisplayName(conversation.customer)}
                  </Text>
                  <div className={conversationInfoCardCss.unreadIndicator}>
                    {conversation.read ? null : (
                      <div className={conversationInfoCardCss.unread} />
                    )}
                  </div>
                </div>
                {lastMessage?.sentAt ? (
                  <Tooltip
                    arrow
                    title={formatDateTime(
                      Temporal.Instant.from(lastMessage.sentAt),
                    )}
                  >
                    <Flex w="fit-content">
                      <Text as="span" fontSize="xxs" textColor="tertiary">
                        {timeSinceMessage}
                      </Text>
                    </Flex>
                  </Tooltip>
                ) : (
                  <Text fontSize="xxs" textColor="tertiary">
                    {timeSinceMessage}
                  </Text>
                )}
              </div>
              <div className={conversationInfoCardCss.content}>
                {conversation?.platform === ConversationPlatform.EMAIL &&
                  conversation.subject && (
                    <div className={conversationInfoCardCss.oneLine}>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        textColor="primary"
                      >
                        {conversation.subject}
                      </Text>
                    </div>
                  )}
                <div
                  className={classNames(
                    conversationInfoCardCss.twoLines,
                    conversationInfoCardCss.gray,
                  )}
                >
                  <Text fontSize="xs" textColor="tertiary">
                    {[...(contentLines || [])].join(" ")}
                  </Text>
                </div>
              </div>
            </div>
            <div className={conversationInfoCardCss.pillContainer}>
              {conversation && (
                <RedoSupportChannelBadge
                  platform={conversation.platform}
                  size="xs"
                />
              )}
              <RedoSupportStatusBadge
                size="xs"
                snoozedUntil={conversation.snoozedUntil}
                status={getConversationStatus(conversation)}
              />
            </div>

            {activeUsers.length > 0 && (
              <ActiveUsers conversation={conversation} maxAvatars={3} />
            )}
          </div>
        )}
        {empty && (
          <Text
            className={conversationInfoCardCss.empty}
            fontSize="xs"
            textColor="disabled"
          >
            No conversations
          </Text>
        )}
        {loading && (
          <div className={conversationInfoCardCss.loadingCard}>
            <SkeletonText length={20} />
            <SkeletonText length={8} />
            <SkeletonText length={30} />
          </div>
        )}
      </div>
    );
  },
);
